import { defineMessages } from 'react-intl';

const dayMessages = defineMessages({
  sunday: { id: 'sku-add-menu-modal--day-sunday', defaultMessage: 'Sunday' },
  monday: { id: 'sku-add-menu-modal--day-monday', defaultMessage: 'Monday' },
  tuesday: { id: 'sku-add-menu-modal--day-tuesday', defaultMessage: 'Tuesday' },
  wednesday: { id: 'sku-add-menu-modal--day-wednesday', defaultMessage: 'Wednesday' },
  thursday: { id: 'sku-add-menu-modal--day-thursday', defaultMessage: 'Thursday' },
  friday: { id: 'sku-add-menu-modal--day-friday', defaultMessage: 'Friday' },
  saturday: { id: 'sku-add-menu-modal--day-saturday', defaultMessage: 'Saturday' },
});

export const validationMessages = defineMessages({
  startEndTimeEqual: {
    id: 'sku-add-menu-modal--start-end-time-equal',
    defaultMessage: 'Start time cannot be the same as end time.',
  },
  startAfterEnd: {
    id: 'sku-add-menu-modal--start-after-end',
    defaultMessage: 'Start time must be before end time.',
  },
  overlappingTimes: {
    id: 'sku-add-menu-modal--overlapping-times',
    defaultMessage: 'Time periods cannot overlap. Please adjust your selected times.',
  },
  noEnabledDays: {
    id: 'sku-add-menu-modal--no-enabled-days',
    defaultMessage: 'Please add at least one menu hour.',
  },
  noTimeRange: {
    id: 'sku-add-menu-modal--no-time-range',
    defaultMessage: 'Each enabled day must include at least one time range.',
  },
});

export const DAY_OPTIONS = [
  { key: 'SUNDAY', labelMessage: dayMessages.sunday },
  { key: 'MONDAY', labelMessage: dayMessages.monday },
  { key: 'TUESDAY', labelMessage: dayMessages.tuesday },
  { key: 'WEDNESDAY', labelMessage: dayMessages.wednesday },
  { key: 'THURSDAY', labelMessage: dayMessages.thursday },
  { key: 'FRIDAY', labelMessage: dayMessages.friday },
  { key: 'SATURDAY', labelMessage: dayMessages.saturday },
];

export const DEFAULT_START_TIME = '08:00';
export const DEFAULT_END_TIME = '17:00';

let rangeCounter = 0;

export const createDefaultRange = () => {
  rangeCounter += 1;
  return {
    id: `range-${rangeCounter}`,
    start: DEFAULT_START_TIME,
    end: DEFAULT_END_TIME,
  };
};

export const createInitialHoursState = () =>
  DAY_OPTIONS.reduce((acc, day) => {
    acc[day.key] = {
      enabled: day.key !== 'SUNDAY',
      ranges: [createDefaultRange()],
    };
    return acc;
  }, {});

export const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const totalMinutes = index * 30;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  const date = new Date(2000, 0, 1, hours, minutes);

  return {
    value,
    date,
  };
});

const timeToMinutes = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const [hour, minute] = value.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }

  return hour * 60 + minute;
};

export const buildBusinessHoursPayload = hoursState =>
  DAY_OPTIONS.flatMap(day => {
    const dayState = hoursState?.[day.key];
    if (!dayState || !Array.isArray(dayState.ranges)) {
      return [];
    }

    return dayState.ranges
      .map(range => {
        const startMinutes = timeToMinutes(range.start);
        const endMinutes = timeToMinutes(range.end);
        if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) {
          return null;
        }

        const startHour = Math.floor(startMinutes / 60);
        const startMinute = startMinutes % 60;
        const endHour = Math.floor(endMinutes / 60);
        const endMinute = endMinutes % 60;

        return {
          dayOfWeek: day.key,
          startTime: {
            hours: startHour,
            minutes: startMinute,
            seconds: 0,
          },
          endTime: {
            hours: endHour,
            minutes: endMinute,
            seconds: 0,
          },
          enabled: dayState.enabled,
        };
      })
      .filter(Boolean);
  });

const PROTO_DAY_OF_WEEK_TO_STRING = {
  0: null,
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY',
};

export const normalizeDayOfWeek = value => {
  if (typeof value === 'string' && DAY_OPTIONS.some(day => day.key === value)) {
    return value;
  }
  if (typeof value === 'number') {
    return PROTO_DAY_OF_WEEK_TO_STRING[value] || null;
  }
  return null;
};

const snapToHalfHour = totalMinutes => {
  const snapped = Math.round(totalMinutes / 30) * 30;
  const clamped = Math.min(snapped, 23 * 60 + 30);
  return { hours: Math.floor(clamped / 60), minutes: clamped % 60 };
};

const timeOfDayToString = time => {
  if (!time || typeof time !== 'object') {
    return null;
  }

  const rawHours = typeof time.hours === 'number' ? time.hours : 0;
  const rawMinutes = typeof time.minutes === 'number' ? time.minutes : 0;
  const { hours, minutes } = snapToHalfHour(rawHours * 60 + rawMinutes);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const buildHoursStateFromPayload = businessHours => {
  const state = DAY_OPTIONS.reduce((acc, day) => {
    acc[day.key] = { enabled: false, ranges: [] };
    return acc;
  }, {});

  if (Array.isArray(businessHours)) {
    businessHours.forEach(entry => {
      const dayKey = normalizeDayOfWeek(entry.dayOfWeek);
      if (!dayKey || !state[dayKey]) {
        return;
      }

      const start = timeOfDayToString(entry.startTime);
      const end = timeOfDayToString(entry.endTime);
      if (!start || !end) {
        return;
      }

      state[dayKey].enabled = entry.enabled !== false;
      state[dayKey].ranges.push({
        id: `range-${(rangeCounter += 1)}`,
        start,
        end,
      });
    });
  }

  DAY_OPTIONS.forEach(day => {
    if (state[day.key].ranges.length === 0) {
      state[day.key].ranges = [createDefaultRange()];
    }
  });

  return state;
};

export const getHoursValidationError = hoursState => {
  const enabledDays = DAY_OPTIONS.filter(day => hoursState?.[day.key]?.enabled);
  if (enabledDays.length === 0) {
    return { messageDescriptor: validationMessages.noEnabledDays };
  }

  return (
    enabledDays.reduce((dayError, day) => {
      if (dayError) {
        return dayError;
      }

      const dayState = hoursState[day.key];
      const ranges = Array.isArray(dayState?.ranges) ? dayState.ranges : [];
      if (ranges.length === 0) {
        return {
          messageDescriptor: validationMessages.noTimeRange,
          dayKey: day.key,
        };
      }

      const intervals = [];
      const invalidRangeIndex = ranges.findIndex(range => {
        const start = timeToMinutes(range.start);
        const end = timeToMinutes(range.end);
        if (start === null || end === null || start >= end) {
          return true;
        }
        intervals.push([start, end]);
        return false;
      });
      if (invalidRangeIndex >= 0) {
        const invalidRange = ranges[invalidRangeIndex];
        const invalidStart = timeToMinutes(invalidRange?.start);
        const invalidEnd = timeToMinutes(invalidRange?.end);

        return {
          messageDescriptor:
            invalidStart === invalidEnd ? validationMessages.startEndTimeEqual : validationMessages.startAfterEnd,
          dayKey: day.key,
          rangeIndex: invalidRangeIndex,
          highlight: 'range',
        };
      }

      intervals.sort((a, b) => a[0] - b[0]);
      const hasOverlap = intervals.some((interval, index) => index > 0 && interval[0] < intervals[index - 1][1]);
      if (hasOverlap) {
        return { messageDescriptor: validationMessages.overlappingTimes, dayKey: day.key };
      }

      return null;
    }, null) || null
  );
};
